import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import type { AuthUser, LoginCredentials, LoginResponse } from '../types/auth';

interface UseAuthConfig {
  apiUrl: string;
  storagePrefix?: string;
  onTokenChange?: (token: string | null) => void;
  onUserChange?: (user: AuthUser | null) => void;
}

export function createUseAuth(config: UseAuthConfig) {
  const { apiUrl, storagePrefix = 'auth', onTokenChange, onUserChange } = config;
  
  const TOKEN_KEY = `${storagePrefix}_token`;
  const USER_KEY = `${storagePrefix}_user`;
  const DOMAIN_KEY = `${storagePrefix}_domain`;

  return function useAuth() {
    const [user, setUser] = useState<AuthUser | null>(() => {
      const savedUser = localStorage.getItem(USER_KEY);
      return savedUser ? JSON.parse(savedUser) : null;
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Notificar cambios de token
    useEffect(() => {
      const savedToken = localStorage.getItem(TOKEN_KEY);
      if (onTokenChange) {
        onTokenChange(savedToken);
      }
    }, []);

    // Notificar cambios de usuario
    useEffect(() => {
      if (onUserChange) {
        onUserChange(user);
      }
    }, [user]);

    const login = useCallback(
      async (credentials: LoginCredentials) => {
        setIsLoading(true);
        setError(null);

        try {
          const response = await axios.post<LoginResponse>(`${apiUrl}/auth/login`, {
            email: credentials.email,
            password: credentials.password,
            domain: credentials.domain,
          });

          const { access_token, user: userData } = response.data;

          // Guardar en localStorage
          localStorage.setItem(TOKEN_KEY, access_token);
          localStorage.setItem(USER_KEY, JSON.stringify(userData));
          localStorage.setItem(DOMAIN_KEY, credentials.domain);

          // Notificar cambio de token
          if (onTokenChange) {
            onTokenChange(access_token);
          }

          // Actualizar estado
          setUser(userData);

          return userData;
        } catch (err: any) {
          const errorMessage = err.response?.data?.message || 'Error al iniciar sesión';
          setError(errorMessage);
          throw new Error(errorMessage);
        } finally {
          setIsLoading(false);
        }
      },
      [apiUrl]
    );

    const logout = useCallback(() => {
      // Limpiar localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(DOMAIN_KEY);

      // Notificar cambio de token
      if (onTokenChange) {
        onTokenChange(null);
      }

      // Limpiar estado
      setUser(null);
    }, []);

    // Obtener token actual
    const getToken = useCallback(() => {
      return localStorage.getItem(TOKEN_KEY);
    }, []);

    // Obtener dominio actual
    const getDomain = useCallback(() => {
      return localStorage.getItem(DOMAIN_KEY);
    }, []);

    // Escuchar eventos de unauthorized
    useEffect(() => {
      const handleUnauthorized = () => {
        logout();
      };

      window.addEventListener('auth:unauthorized', handleUnauthorized);
      return () => {
        window.removeEventListener('auth:unauthorized', handleUnauthorized);
      };
    }, [logout]);

    return {
      user,
      isLoading,
      error,
      login,
      logout,
      getToken,
      getDomain,
      isAuthenticated: !!user,
    };
  };
}

// Hook por defecto con configuración estándar
export function useAuth() {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';
  const useAuthHook = createUseAuth({ apiUrl });
  return useAuthHook();
}









